import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Avatar, Tabs, Tab, Grid, Card, CardContent,
  Switch, Divider, FormControl, Select, MenuItem, IconButton, Tooltip, alpha,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle, Cancel, Visibility, VerifiedUser, Warning, Security,
  Description, Upload, Shield, Block, Fingerprint, Refresh, Search,
  Settings, Policy, GppBad, GppGood, HowToReg, PersonSearch,
} from '@mui/icons-material';
import { adminAPI, settingsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
const statusColors = { pending: 'warning', approved: 'success', rejected: 'error', suspended: 'default' };

function AdminKYCPage() {
  const [kycs, setKycs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [viewKyc, setViewKyc] = useState(null);
  const [docPreview, setDocPreview] = useState(null);

  const [kycSettings, setKycSettings] = useState({
    auto_verify_enabled: 'false', selfie_required: 'true', address_verification: 'true',
    allowed_id_types: 'passport,national_id,student_id,drivers_license',
    max_file_size_mb: '10', allowed_file_types: 'jpg,jpeg,png,pdf',
    duplicate_detection: 'true', blacklist_enabled: 'true',
    data_retention_days: '365', encryption_enabled: 'true',
    resubmission_allowed: 'true', max_resubmissions: '3',
  });

  const fetchKYC = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listAllKYC({ page, per_page: 15 });
      setKycs(res.data.kyc_verifications || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      // Fallback to pending endpoint if all endpoint fails
      try {
        const res = await adminAPI.listPendingKYC({ page, per_page: 15 });
        setKycs(res.data.kyc_verifications || []);
        setTotal(res.data.total || 0);
      } catch (_) { console.error(err); }
    }
    finally { setLoading(false); }
  };

  const handleViewKyc = async (kyc) => {
    // Try fetching full detail with user info and all document URLs
    try {
      const res = await adminAPI.getKYCDetail(kyc.id);
      setViewKyc(res.data);
    } catch (_) {
      setViewKyc(kyc); // Fallback to list data
    }
  };

  useEffect(() => { fetchKYC(); }, [page]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await settingsAPI.list({ category: 'kyc' });
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(s => { map[s.key] = s.value; });
          setKycSettings(prev => ({ ...prev, ...map }));
        }
      } catch (_) {}
    };
    loadSettings();
  }, []);

  const handleApprove = async (kycId) => {
    try {
      await adminAPI.approveKYC(kycId);
      setSuccess('KYC approved successfully');
      fetchKYC();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectReason) return;
    try {
      await adminAPI.rejectKYC(rejectDialog, rejectReason);
      setRejectDialog(null);
      setRejectReason('');
      setSuccess('KYC rejected');
      fetchKYC();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to reject'); }
  };

  const handleSettingUpdate = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setKycSettings(prev => ({ ...prev, [key]: String(value) }));
      setSuccess(`Setting updated`);
    } catch (_) { setError('Failed to update setting'); }
  };

  const pendingCount = kycs.filter(k => k.status === 'pending').length;
  const approvedCount = kycs.filter(k => k.status === 'approved').length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #10b981, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            KYC Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">Identity verification for students, landlords, and agents</Typography>
        </Box>
        <IconButton onClick={fetchKYC} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
          sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Review Queue" icon={<HowToReg sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Verification Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Risk & Compliance" icon={<Shield sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Review Queue */}
      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Pending Review', count: pendingCount, color: '#f59e0b', icon: <Warning /> },
              { label: 'Approved', count: approvedCount, color: '#10b981', icon: <GppGood /> },
              { label: 'Rejected', count: kycs.filter(k => k.status === 'rejected').length, color: '#ef4444', icon: <GppBad /> },
              { label: 'Total Submissions', count: total, color: '#6366f1', icon: <Fingerprint /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography variant="h5" fontWeight={800}>{item.count}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>ID Type</TableCell>
                  <TableCell>ID Number</TableCell>
                  <TableCell>Selfie</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : kycs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography fontWeight={600}>No pending verifications</Typography>
                      <Typography variant="body2" color="text.secondary">All KYC submissions have been reviewed</Typography>
                    </Box>
                  </TableCell></TableRow>
                ) : kycs.map((kyc) => (
                  <TableRow key={kyc.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                          {kyc.user?.first_name?.[0] || kyc.user_id?.toString().slice(0, 1)?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          {kyc.user ? (
                            <>
                              <Typography variant="body2" fontWeight={600} noWrap>{kyc.user.first_name} {kyc.user.last_name}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>{kyc.user.email}</Typography>
                            </>
                          ) : (
                            <Typography variant="body2" fontWeight={500}>{kyc.user_id?.toString().slice(0, 8)}...</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={kyc.id_type?.replace('_', ' ') || 'N/A'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{kyc.id_number || 'N/A'}</Typography></TableCell>
                    <TableCell>
                      {kyc.selfie_url
                        ? <Chip label="Provided" size="small" color="success" variant="outlined" />
                        : <Chip label="Missing" size="small" color="warning" variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      <Chip label={kyc.status} size="small" color={statusColors[kyc.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>{new Date(kyc.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Documents">
                          <IconButton size="small" onClick={() => handleViewKyc(kyc)}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        {kyc.status === 'pending' && (
                          <>
                            <Button size="small" color="success" variant="contained" startIcon={<CheckCircle />}
                              onClick={() => handleApprove(kyc.id)} sx={{ px: 1.5 }}>Approve</Button>
                            <Button size="small" color="error" variant="outlined" startIcon={<Cancel />}
                              onClick={() => setRejectDialog(kyc.id)} sx={{ px: 1.5 }}>Reject</Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 15 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={Math.ceil(total / 15)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Tab 1: Verification Settings */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Verification Requirements</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Allowed ID Types</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {['passport', 'national_id', 'ghana_card', 'student_id', 'drivers_license', 'voters_card', 'nhis_card'].map(type => (
                      <Chip key={type} label={type.replace('_', ' ')} size="small"
                        color={kycSettings.allowed_id_types?.includes(type) ? 'primary' : 'default'}
                        variant={kycSettings.allowed_id_types?.includes(type) ? 'filled' : 'outlined'}
                        onClick={() => {
                          const current = kycSettings.allowed_id_types?.split(',') || [];
                          const updated = current.includes(type)
                            ? current.filter(t => t !== type)
                            : [...current, type];
                          handleSettingUpdate('allowed_id_types', updated.join(','));
                        }}
                        sx={{ textTransform: 'capitalize', cursor: 'pointer', mb: 0.5 }} />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Selfie Verification</Typography>
                    <Typography variant="body2" color="text.secondary">Require selfie with ID document</Typography>
                  </Box>
                  <Switch checked={kycSettings.selfie_required === 'true'}
                    onChange={(e) => handleSettingUpdate('selfie_required', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Address Verification</Typography>
                    <Typography variant="body2" color="text.secondary">Require proof of address</Typography>
                  </Box>
                  <Switch checked={kycSettings.address_verification === 'true'}
                    onChange={(e) => handleSettingUpdate('address_verification', e.target.checked ? 'true' : 'false')} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Verification Workflow</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Auto Verification</Typography>
                    <Typography variant="body2" color="text.secondary">Automatically verify matching documents</Typography>
                  </Box>
                  <Switch checked={kycSettings.auto_verify_enabled === 'true'}
                    onChange={(e) => handleSettingUpdate('auto_verify_enabled', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Allow Resubmission</Typography>
                    <Typography variant="body2" color="text.secondary">Users can resubmit after rejection</Typography>
                  </Box>
                  <Switch checked={kycSettings.resubmission_allowed === 'true'}
                    onChange={(e) => handleSettingUpdate('resubmission_allowed', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Max Resubmissions</Typography>
                  <TextField size="small" type="number" value={kycSettings.max_resubmissions}
                    onChange={(e) => setKycSettings(prev => ({ ...prev, max_resubmissions: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('max_resubmissions', e.target.value)}
                    sx={{ width: 100 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Document Management</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Max File Size (MB)</Typography>
                  <TextField size="small" type="number" value={kycSettings.max_file_size_mb}
                    onChange={(e) => setKycSettings(prev => ({ ...prev, max_file_size_mb: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('max_file_size_mb', e.target.value)}
                    sx={{ width: 100 }} />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Allowed File Types</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {['jpg', 'jpeg', 'png', 'pdf', 'webp'].map(type => (
                      <Chip key={type} label={`.${type}`} size="small"
                        color={kycSettings.allowed_file_types?.includes(type) ? 'primary' : 'default'}
                        variant={kycSettings.allowed_file_types?.includes(type) ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer', mb: 0.5 }} />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>KYC Status Definitions</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { status: 'Pending', desc: 'Submitted, awaiting admin review', color: '#f59e0b' },
                  { status: 'Approved', desc: 'Verified and approved by admin', color: '#10b981' },
                  { status: 'Rejected', desc: 'Declined, user may resubmit', color: '#ef4444' },
                  { status: 'Suspended', desc: 'Account flagged, KYC frozen', color: '#64748b' },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeftWidth: 4, borderLeftColor: item.color }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                      <Box>
                        <Typography fontWeight={600}>{item.status}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Risk & Compliance */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Risk & Fraud Controls</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Duplicate ID Detection</Typography>
                    <Typography variant="body2" color="text.secondary">Flag duplicate ID numbers across users</Typography>
                  </Box>
                  <Switch checked={kycSettings.duplicate_detection === 'true'}
                    onChange={(e) => handleSettingUpdate('duplicate_detection', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Blacklist Database</Typography>
                    <Typography variant="body2" color="text.secondary">Check against known fraud database</Typography>
                  </Box>
                  <Switch checked={kycSettings.blacklist_enabled === 'true'}
                    onChange={(e) => handleSettingUpdate('blacklist_enabled', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.03) }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}><Warning /></Avatar>
                    <Box>
                      <Typography fontWeight={600}>Suspicious User Flagging</Typography>
                      <Typography variant="body2" color="text.secondary">Automatically flag users with mismatched data</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Compliance Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Data Retention Period (days)</Typography>
                  <TextField size="small" type="number" value={kycSettings.data_retention_days}
                    onChange={(e) => setKycSettings(prev => ({ ...prev, data_retention_days: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('data_retention_days', e.target.value)}
                    sx={{ width: 120 }} />
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Document Encryption</Typography>
                    <Typography variant="body2" color="text.secondary">Encrypt stored identity documents</Typography>
                  </Box>
                  <Switch checked={kycSettings.encryption_enabled === 'true'}
                    onChange={(e) => handleSettingUpdate('encryption_enabled', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>Privacy Compliance</Typography>
                  <Stack spacing={1}>
                    {['GDPR Compliance', 'Data Protection Act', 'Right to be Forgotten'].map(policy => (
                      <Stack key={policy} direction="row" alignItems="center" spacing={1}>
                        <CheckCircle color="success" sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{policy}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* View KYC Dialog */}
      <Dialog open={!!viewKyc} onClose={() => setViewKyc(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          KYC Document Review
          {viewKyc?.status === 'pending' && (
            <Stack direction="row" spacing={1}>
              <Button color="success" variant="contained" size="small" startIcon={<CheckCircle />} onClick={() => { handleApprove(viewKyc.id); setViewKyc(null); }}>Approve</Button>
              <Button color="error" variant="outlined" size="small" startIcon={<Cancel />} onClick={() => { setRejectDialog(viewKyc.id); setViewKyc(null); }}>Reject</Button>
            </Stack>
          )}
        </DialogTitle>
        <DialogContent>
          {viewKyc && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {(viewKyc.user || viewKyc.user_name) && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Applicant Info</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Name</Typography><Typography fontWeight={600}>{viewKyc.user ? `${viewKyc.user.first_name} ${viewKyc.user.last_name}` : viewKyc.user_name}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography><Typography>{viewKyc.user?.email || viewKyc.user_email || 'N/A'}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography>{viewKyc.user?.phone || viewKyc.user_phone || 'N/A'}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Role</Typography><Chip label={(viewKyc.user?.role || viewKyc.user_role || '').replace('_',' ')} size="small" sx={{ textTransform: 'capitalize' }} /></Grid>
                  </Grid>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">ID Type</Typography><Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>{viewKyc.id_type?.replace('_', ' ')}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">ID Number</Typography><Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>{viewKyc.id_number}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip label={viewKyc.status} size="small" color={statusColors[viewKyc.status]} sx={{ textTransform: 'capitalize' }} /></Box></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Submitted</Typography><Typography>{new Date(viewKyc.created_at).toLocaleString()}</Typography></Grid>
              </Grid>

              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>Uploaded Documents</Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'ID Front', url: viewKyc.id_front_url || viewKyc.document_url },
                  { label: 'ID Back', url: viewKyc.id_back_url },
                  { label: 'Selfie Photo', url: viewKyc.selfie_url },
                  { label: 'Proof of Address', url: viewKyc.proof_of_address_url },
                  { label: 'Student ID', url: viewKyc.student_id_url },
                  { label: 'Admission Letter', url: viewKyc.admission_letter_url },
                  { label: 'Property Ownership', url: viewKyc.property_ownership_doc_url },
                  { label: 'Business Registration', url: viewKyc.business_registration_url },
                ].filter(doc => doc.url).map((doc, i) => {
                  const fullUrl = doc.url.startsWith('http') ? doc.url : `${API_BASE}${doc.url}`;
                  const isPdf = doc.url.match(/\.pdf$/i);
                  const isImage = doc.url.match(/\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i);
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" fontWeight={600}>{doc.label}</Typography>
                          <Tooltip title="Preview document">
                            <IconButton size="small" onClick={() => setDocPreview({ url: fullUrl, label: doc.label, isPdf, isImage })}><Visibility fontSize="small" /></IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ p: 1, textAlign: 'center', bgcolor: '#fafafa', minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isImage ? (
                            <Box
                              component="img"
                              src={fullUrl}
                              alt={doc.label}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              sx={{ maxWidth: '100%', maxHeight: 350, borderRadius: 1, cursor: 'pointer', objectFit: 'contain' }}
                              onClick={() => setDocPreview({ url: fullUrl, label: doc.label, isPdf, isImage })}
                            />
                          ) : isPdf ? (
                            <Box sx={{ width: '100%', height: 350 }}>
                              <iframe
                                src={`${fullUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                                title={doc.label}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', borderRadius: 4 }}
                              />
                            </Box>
                          ) : (
                            <Stack alignItems="center" spacing={1}>
                              <Description sx={{ fontSize: 48, color: 'grey.400' }} />
                              <Button variant="outlined" size="small" startIcon={<Visibility />}
                                onClick={() => setDocPreview({ url: fullUrl, label: doc.label, isPdf, isImage })}>
                                View Document
                              </Button>
                            </Stack>
                          )}
                          {isImage && (
                            <Stack alignItems="center" spacing={1} sx={{ display: 'none' }}>
                              <Description sx={{ fontSize: 48, color: 'grey.400' }} />
                              <Typography variant="body2" color="text.secondary">Could not load image</Typography>
                              <Button variant="outlined" size="small" startIcon={<Visibility />}
                                onClick={() => setDocPreview({ url: fullUrl, label: doc.label, isPdf: false, isImage: false })}>
                                Open Anyway
                              </Button>
                            </Stack>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {viewKyc.rejection_reason && (
                <Alert severity="error">Rejection Reason: {viewKyc.rejection_reason}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewKyc?.status === 'pending' && (
            <>
              <Button color="success" variant="contained" onClick={() => { handleApprove(viewKyc.id); setViewKyc(null); }}>Approve</Button>
              <Button color="error" variant="outlined" onClick={() => { setRejectDialog(viewKyc.id); setViewKyc(null); }}>Reject</Button>
            </>
          )}
          <Button onClick={() => setViewKyc(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!docPreview} onClose={() => setDocPreview(null)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          {docPreview?.label || 'Document Preview'}
          <Button size="small" variant="outlined" onClick={() => setDocPreview(null)}>Close</Button>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#1a1a1a', p: 0 }}>
          {docPreview?.isImage ? (
            <Box component="img" src={docPreview.url} alt={docPreview.label}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : docPreview?.isPdf ? (
            <iframe src={`${docPreview.url}#toolbar=1&navpanes=0&scrollbar=1`}
              title={docPreview.label} width="100%" height="100%" style={{ border: 'none' }} />
          ) : (
            <iframe src={docPreview?.url} title={docPreview?.label}
              width="100%" height="100%" style={{ border: 'none' }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject KYC Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Please provide a reason for rejection:</Typography>
          <TextField fullWidth multiline rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Document unclear, ID expired, information mismatch..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectReason}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminKYCPage;
